import { Link } from "@tanstack/react-router";
import { waitLabel, type QueueItem } from "@/app/reviewQueries";
import { Avatar } from "@/app/nav";
import { count } from "@/lib/plural";
import { FlagChip } from "./Rows";
import { cohortLabel, isFailing, isManual, iterLabel } from "./model";

const fmtDate = (iso: string) => new Date(iso).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

/** Тексты макета M1 «на что смотреть». */
export const HINTS = [
  "Красный раннер — не повод возвращать без комментария: скажи, где именно процедура неверна",
  "Зелёные автопроверки не значат принято: смотри разбиение данных и утечки",
  "Третья итерация подряд — повод позвать на созвон, а не писать четвёртый текст",
  "Студенты вне когорт живут в той же очереди: у них нет общего дедлайна, но SLA такой же",
];

export const RETURN_HINT = "вернуть можно только с комментариями к ячейкам — откройте ревью";

const VALUE_CLASS = { ink2: "text-ink2", err: "text-err", warn: "text-warn" } as const;

function Row({ k, v, tone = "ink2" }: { k: string; v: string; tone?: keyof typeof VALUE_CLASS }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-bd py-[7px] font-mono text-[12px] last:border-0">
      <span className="text-mut">{k}</span>
      <span className={`text-right ${VALUE_CLASS[tone]}`}>{v}</span>
    </div>
  );
}

/** Полоса прогона из счётчиков (не позиций задач): прошло · упало · не запускалось. */
function RunStrip({ r }: { r: QueueItem }) {
  const cells = [...Array<string>(r.passed).fill("passed"), ...Array<string>(r.failed).fill("failed"), ...Array<string>(r.idle).fill("idle")];
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex gap-[2px]" aria-hidden>
        {cells.map((s, i) => (
          <span
            key={i}
            className="h-2 flex-1 rounded-[1px]"
            style={s === "passed" ? { background: "var(--acc)" } : s === "failed" ? { background: "var(--err)" } : { border: "1px dashed var(--bd2)" }}
          />
        ))}
      </div>
      <span className="font-mono text-[10.5px] text-ink2" data-testid="run-counts">
        {r.passed} прошло · {r.failed} упало · {r.idle} не запускалось
      </span>
    </div>
  );
}

/** Карточка выбранной сдачи. «вернуть» не обходит правило M2 (§2.1): это ссылка в ревью с подсказкой. */
export function SelectedCard({ r, showMentor }: { r?: QueueItem; showMentor: boolean }) {
  if (!r) {
    return (
      <section data-testid="selected-card" data-empty className="rounded-lg border border-dashed border-bd p-4 font-mono text-[12px] leading-[1.5] text-mut">
        Выберите сдачу в списке — здесь появятся её детали, ссылка на ревью и подсказка по возврату.
      </section>
    );
  }
  const manual = isManual(r);
  return (
    <section data-testid="selected-card" className="overflow-hidden rounded-lg border border-bd bg-card">
      <div className="flex items-center gap-3 border-b border-bd bg-bg2 px-4 py-3">
        <Avatar name={r.student.name} />
        <div className="flex min-w-0 flex-col">
          <Link to="/mentor/student/$userId" params={{ userId: r.student.id }} className="truncate text-[13.5px] font-semibold text-ink no-underline hover:text-acc hover:underline" data-testid="selected-student">
            {r.student.name}
          </Link>
          <span className={`font-mono text-[10.5px] ${r.cohort ? "text-mut" : "text-ink2"}`}>{cohortLabel(r)}</span>
        </div>
      </div>
      <div className="flex flex-col gap-3 px-4 py-3.5">
        <span className="font-mono text-[12.5px] text-ink">{r.assignment}</span>
        <div>
          <Row k="ждёт" v={r.late ? `${waitLabel(r.wait_hours)} · за пределами SLA` : waitLabel(r.wait_hours)} tone={r.late ? "err" : "ink2"} />
          <Row k="автопроверки" v={manual ? "нет раннера" : `${r.passed} / ${r.total}`} tone={isFailing(r) ? "err" : "ink2"} />
          <Row k="итерация" v={iterLabel(r)} />
          <Row k="отправлено" v={fmtDate(r.submitted_at)} />
          {showMentor && <Row k="ментор" v={r.mentor?.name ?? "не назначен"} tone={r.mentor ? "ink2" : "warn"} />}
        </div>
        {manual ? (
          <span className="font-mono text-[11px] text-ink2">у этой сдачи нет автопроверок — только ручное ревью</span>
        ) : (
          <>
            <RunStrip r={r} />
            <span className="font-mono text-[11px] text-ink2">результат последнего прогона студента</span>
          </>
        )}
        {r.flag && (
          <div className="flex flex-col gap-1.5 rounded-sm border border-warn bg-warnsoft px-3 py-2.5" data-testid="selected-flag">
            <FlagChip flag={r.flag} />
            <span className="text-[12px] leading-[1.45] text-ink">
              {count(r.flag.count, "возврат", "возврата", "возвратов")} подряд по причине «{r.flag.reason.label}». Третья итерация по одной причине — повод на созвон, а не четвёртый текст.
            </span>
          </div>
        )}
        <div className="flex flex-col gap-2 pt-1">
          <Link
            to="/mentor/review/$submissionId"
            params={{ submissionId: r.submission_id }}
            className="inline-flex w-full items-center justify-center rounded-sm border border-acc bg-acc px-4 py-[9px] font-mono text-[13px] font-medium text-accink no-underline hover:brightness-[1.08]"
            data-testid="open-review"
          >
            открыть ревью
          </Link>
          <Link
            to="/mentor/review/$submissionId"
            params={{ submissionId: r.submission_id }}
            className="inline-flex w-full items-center justify-center rounded-sm border border-err bg-transparent px-4 py-[9px] font-mono text-[13px] text-err no-underline hover:bg-errsoft"
            data-testid="quick-return"
          >
            вернуть
          </Link>
          <span className="font-mono text-[11px] leading-[1.45] text-ink2" data-testid="return-hint">
            {RETURN_HINT}
          </span>
        </div>
      </div>
    </section>
  );
}

export function HintsCard() {
  return (
    <section className="rounded-lg border border-bd bg-card px-4 py-3.5" data-testid="hints-card">
      <div className="mb-2 font-mono text-[10.5px] uppercase tracking-[.06em] text-mut">на что смотреть</div>
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {HINTS.map((h) => (
          <li key={h} className="flex gap-2 text-[12.5px] leading-[1.5] text-ink2">
            <span aria-hidden className="font-mono text-accdim">
              ·
            </span>
            <span>{h}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
